import {map} from 'rxjs/operators';
import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  ElementRef,
  NgZone,
  ViewChild,
} from '@angular/core';
import {ViewTabComponent} from '../../viewtab.component';
import {ErrorService} from '../../../../error/error.service';
import {Observable} from 'rxjs';
import {CacheService} from '../../../../cache/cache.service';
import {ResourceService} from '../../../resource.service';
import {EnvironmentConfigService} from '../../../../core/services/environment-config.service';
import {MatDialog} from '@angular/material/dialog';
import {COMMA, ENTER} from '@angular/cdk/keycodes';
import {LanguageService} from '../../../../language/language.service';
import {AbstractAttributeFlowControl, SourceType} from './abstract-attribute-flow-control';
import {ImportFlow} from './attribute-flow';

@Component({
  selector: 'ctrl-inbound-attribute-flow',
  styleUrls: ['./ctrl-synchronization.scss'],
  template: `
    <ctrl-attribute-flow-title
      [title]="getCaughtObservable(listViewTitle)"
      [control]="formControl"
      [enabled]="getCaughtObservable(isEnabled)"
    ></ctrl-attribute-flow-title>
    <table class="table mat-elevation-z8">
      <thead>
        <tr>
          <th>
            <language
              key="CtrlInboundAttributeFlowColumnSource"
              defaultValue="Flow Source: External System Attributes/Values"
            ></language>
          </th>
          <th>
            <language
              key="CtrlInboundAttributeFlowColumnDestination"
              defaultValue="Flow Destination: FIM Attribute"
            ></language>
          </th>
          <th class="buttons">
            <button
              mat-icon-button
              (click)="addRow()"
              color="accent"
              [disabled]="(getCaughtObservable(isEnabled) | async) != true"
            >
              <mat-icon arial-label="Add">add</mat-icon>
            </button>
          </th>
        </tr>
      </thead>
      <tbody>
        @for (entry of value; track entry; let i = $index) {
          <tr class="entry">
            <td>
              @if ((getCaughtObservable(isEnabled) | async) != true) {
                <mat-chip-set>
                  @for (src of entry.src; track src; let srcI = $index) {
                    <mat-chip-row [removable]="false" [class.clickable]="false">
                      {{ src.summary }}
                    </mat-chip-row>
                  }
                </mat-chip-set>
              }
              <mat-form-field [hidden]="(getCaughtObservable(isEnabled) | async) != true">
                <mat-chip-grid #chipList>
                  @for (src of entry.src; track src; let srcI = $index) {
                    <mat-chip-row
                      [removable]="true"
                      (removed)="srcRemoved(entry, srcI)"
                      (click)="srcClick(src)"
                      [class.clickable]="srcClickable(src)"
                    >
                      {{ src.summary }}
                      <mat-icon matChipRemove>cancel</mat-icon>
                    </mat-chip-row>
                  }
                  <input
                    #srcInput
                    [placeholder]="addSourcePlaceholder | async"
                    [formControl]="srcCtrl"
                    [matAutocomplete]="srcAuto"
                    [matChipInputFor]="chipList"
                    [matChipInputSeparatorKeyCodes]="separatorKeysCodes"
                  />
                </mat-chip-grid>
                <mat-autocomplete
                  #srcAuto="matAutocomplete"
                  (optionSelected)="srcSelected(entry, $event)"
                >
                  @for (optGrp of filteredSourceOptions | async; track optGrp) {
                    <mat-optgroup [label]="optGrp.group">
                      @for (opt of optGrp.sources; track opt) {
                        <mat-option [value]="opt">
                          {{ opt.displayName }}
                        </mat-option>
                      }
                    </mat-optgroup>
                  }
                </mat-autocomplete>
              </mat-form-field>
            </td>
            <td>
              <attribute-flow-destination
                [placeholder]="ilmAttributePlaceholder | async"
                [value]="entry.dest"
                (valueChange)="entry.dest = $event; formControl.updateValueAndValidity()"
                (onError)="handleError($event.err, $event.caught)"
                [disabled]="(getCaughtObservable(isEnabled) | async) != true"
                [options]="ilmAttributes"
              ></attribute-flow-destination>
            </td>
            <td class="buttons">
              <button
                mat-icon-button
                (click)="delete(i)"
                [disabled]="(getCaughtObservable(isEnabled) | async) != true"
              >
                <mat-icon aria-label="Delete">close</mat-icon>
              </button>
            </td>
          </tr>
        }
        <tr>
          <td></td>
          <td></td>
          <th class="buttons">
            <button
              mat-icon-button
              (click)="addRow()"
              color="accent"
              [disabled]="(getCaughtObservable(isEnabled) | async) != true"
            >
              <mat-icon arial-label="Add">add</mat-icon>
            </button>
          </th>
        </tr>
      </tbody>
    </table>
  `,
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: false,
})
export class CtrlInboundAttributeFlowComponent extends AbstractAttributeFlowControl {
  constructor(
    parent: ViewTabComponent,
    changeDetector: ChangeDetectorRef,
    _ngZone: NgZone,
    errorService: ErrorService,
    cacheService: CacheService,
    resourceService: ResourceService,
    dialog: MatDialog,
    envConfigService: EnvironmentConfigService,
  ) {
    super(
      parent,
      'PersistentFlow',
      'cs',
      ImportFlow,
      changeDetector,
      _ngZone,
      errorService,
      cacheService,
      resourceService,
      dialog,
      envConfigService,
    );

    this.addSourcePlaceholder = cacheService.getLanguageResource().pipe(
      map(lr => {
        return LanguageService.translate(
          'CtrlInboundAttributeFlowAddSourcePlaceholder',
          'Add source…',
          lr,
        );
      }),
    );
    this.ilmAttributePlaceholder = cacheService.getLanguageResource().pipe(
      map(lr => {
        return LanguageService.translate(
          'CtrlInboundAttributeFlowSelectDestinationPlaceholder',
          'Please select an attribute.',
          lr,
        );
      }),
    );
  }

  addSourcePlaceholder: Observable<string>;
  ilmAttributePlaceholder: Observable<string>;

  ilmAttributes: Observable<string[]>;
  csAttributes: Observable<string[]>;

  separatorKeysCodes: number[] = [ENTER, COMMA];
  filteredSourceOptions: Observable<{group: string; sources: SourceType[]}[]>;

  @ViewChild('srcInput') srcInput: ElementRef<HTMLInputElement>;
}
